// OA: Disabled some linting rules for this ancient file in order to avoid
// lots of rework.
/* eslint-disable @typescript-eslint/no-unnecessary-condition */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-return */
import InterfaceArrowsVerticalExpand1 from '@streamlinehq/streamlinehq/img/streamline-mini-line/interface-essential/arrows/interface-arrows-vertical-expand-1.svg';
import { range } from 'lodash';
import { ReactNode, Component, Fragment } from 'react';
import styled from 'styled-components';
import { FooterCell, HeaderCell, NumberCell, Table } from '../../../../upright/model/output/tables/table-styles';
import { AwesomeActionIcon } from '../../../components/icons/awesome-action-icon';
import { BouColor } from '../../../global/bou-colors';
import { BouSpacingLevel, fluidSpacing } from '../../../layout/bou-spacing';
import { isScreenshotMode } from '../../../utilities/debug-mode';
import { disableTextSelection } from '../../../utilities/fonts';
import { getBouLinkStyle } from '../../texts/bou-link';
import { NiceTitle } from '../../texts/nice-title';
import { DownloadExcelLink, SheetRow } from './download-excel-link';
import { TableLimitOptions } from './types';

interface Props {
  data: TableData;
  limit?: TableLimitOptions;
  sortKey?: string;
  // sort key to default on if no sort key provided or it is invalid
  // (for example because of columns that have been removed from the table)
  defaultSortKey?: string;
  defaultSortOrder?: SortOrder;
  sortOrder?: SortOrder;
  onSortKeyChange?: (key: string) => void;
  onSortOrderChange?: (order: SortOrder) => void;
  disableDownloadLink?: boolean;
  DownloadLink?: JSX.Element;
  disableSort?: boolean;
  disableDefaultSort?: boolean;
  onSortChange?: (data: TableData) => void;
  showFooter: boolean;
  title?: string | JSX.Element | JSX.Element[];
  buttons?: JSX.Element;
  emptyState?: string | JSX.Element;
  extraHeaderRow?: JSX.Element;
  hideHeaderRow?: boolean;
  hideRowLines?: boolean;
  dynamicWidth?: boolean;
  styleOverride?: string;
  footerStyleOverride?: string;

  /**
   * Makes rows appear as selectable
   *
   * This enables cursor: pointer and hover effects,
   * event handling needs to be implemented separately
   */
  enableRowSelection?: boolean;
}

export enum SortOrder {
  ASCENDING = 'asc',
  DESCENDING = 'desc',
}

interface State {
  sortKey: string;
  sortOrder: SortOrder;
  shown: number;
}

const HoverableTitle = styled.div<{ sortable: boolean }>`
  ${props =>
    props.sortable
      ? `
      &:hover {
        color: ${BouColor.TOOL_HOVER_DARK_GRAY};
      }
      cursor: pointer;
      `
      : 'cursor: default'}
`;

const Thead = styled.thead`
  ${disableTextSelection()}
`;

export const LinkWrap = styled.div`
  display: flex;
  justify-content: flex-end;
`;

export interface TableColumn<T> {
  key: string;
  title: string | JSX.Element;
  titleTooltip?: JSX.Element;
  titleAlign?: 'left' | 'right' | 'center';
  excludeFromExport?: boolean;
  exportOnly?: boolean;
  hideBreakpoint?: number;
  renderCell?: (value: T, columnIndex: number, rowIndex?: number, hideBreakpoint?: number) => JSX.Element;
  renderExportValue?: (value: T) => string | number;
  comparator?: (a: T, b: T, order: SortOrder) => number;
  values: T[];
  footerFunction?: (values: T[]) => ReactNode;
}

export type TableData = Array<TableColumn<any>>;

export function toSortComparisonValue(value: number | undefined | null, order: SortOrder) {
  if (value === undefined || value === null) {
    const sign = order === SortOrder.ASCENDING ? 1 : -1;
    return sign * Number.MAX_VALUE;
  }
  return value;
}

export function prepareDataForDownload(sortedData: TableData) {
  const rowCount = sortedData[0].values.length;
  return range(0, rowCount).map(rowIndex => {
    return sortedData.reduce<SheetRow>((result, datum) => {
      const value = datum.values[rowIndex];
      if (!datum.excludeFromExport) {
        result[datum.key] = datum.renderExportValue ? datum.renderExportValue(value) : value;
      }
      return result;
    }, {});
  });
}

export function sortTableData(data: TableData, key: string, order: SortOrder, defaultSortKey?: string): TableData {
  const rowCount = data[0].values.length;
  const dataArray = range(0, rowCount).map(rowIndex => {
    return data.map(datum => {
      const rowData = datum.values[rowIndex];
      return rowData;
    });
  });
  let sortColumnIndex = data.map(d => d.key).indexOf(key);
  let column = data.find(d => d.key === key);
  if (!column) {
    if (defaultSortKey) {
      column = data.find(d => d.key === defaultSortKey);
      sortColumnIndex = data.map(d => d.key).indexOf(defaultSortKey);
      if (!column) {
        throw Error(`Column with default sort key ${defaultSortKey} not found`);
      }
    }
  }
  if (!column) {
    throw Error(`Column with key ${key} not found`);
  }
  const defaultComparator = (a: any, b: any) => toSortComparisonValue(a, order) - toSortComparisonValue(b, order);

  const comparator = column.comparator || defaultComparator;
  const sortedDataArray = dataArray.sort((a, b) => {
    const sign = order === SortOrder.ASCENDING ? 1 : -1;

    const ret = sign * comparator(a[sortColumnIndex], b[sortColumnIndex], order);

    // force definite order for table rows
    // (to avoid live results jumping when they come in random order from database)
    // removed temporarily to avoid error in score-composition-table-new
    // if (ret === 0) {
    //   return JSON.stringify(a).localeCompare(JSON.stringify(b));
    // }
    return ret;
  });

  return data.map((datum, columnIndex) => {
    const values = range(0, rowCount).map(rowIndex => sortedDataArray[rowIndex][columnIndex]);
    return {
      ...datum,
      values,
    };
  });
}

function pickSortKey(props: Props, state: State) {
  return props.sortKey || state.sortKey;
}

function pickSortOrder(props: Props, state: State) {
  return props.sortOrder || state.sortOrder;
}

/**
 * Our old SortableTable that has served well. Don't use this for ANYTHING new. Use TableV2 instead.
 */

export class SortableTable extends Component<Props, State> {
  private explicitlySorted = false;
  public static defaultProps = {
    showFooter: false,
  };

  public constructor(props: Props) {
    super(props);
    this.explicitlySorted = false;
    this.state = {
      sortKey: props.sortKey || props.defaultSortKey || props.data[0].key,
      sortOrder: props.sortOrder || props.defaultSortOrder || SortOrder.DESCENDING,
      shown: 0,
    };
  }

  public componentDidMount() {
    const shown = getInitialCountToShow(this.props.data[0].values.length, this.props.limit);
    this.setState({ shown });
  }

  public componentDidUpdate(prevProps: Props, prevState: State) {
    if (prevProps.data[0].values.length !== this.props.data[0].values.length) {
      const shown = getInitialCountToShow(this.props.data[0].values.length, this.props.limit);
      this.setState({ shown });
    }

    if (
      pickSortKey(prevProps, prevState) !== pickSortKey(this.props, this.state) ||
      pickSortOrder(prevProps, prevState) !== pickSortOrder(this.props, this.state) ||
      prevProps.data.length !== this.props.data.length
    ) {
      if (this.props.onSortChange) {
        this.props.onSortChange(this.getSortedTableData());
      }
    }
  }

  private getSortKey() {
    return pickSortKey(this.props, this.state);
  }

  private getSortOrder() {
    return pickSortOrder(this.props, this.state);
  }

  private renderRows() {
    const { shown } = this.state;

    const sortedData = this.getSortedTableData();
    const totalRowCount = sortedData[0].values.length;
    const shownCount = Math.min(totalRowCount, shown);
    const rows = range(0, shownCount).map(rowIndex => {
      return (
        <tr key={`row-${rowIndex}`}>
          {sortedData
            .filter(datum => !datum.exportOnly)
            .map((datum, columnIndex) => {
              const rowData = datum.values[rowIndex];
              const columnKey = `column-${columnIndex}-${datum.key}`;
              if (datum.renderCell) {
                return (
                  <Fragment key={columnKey}>
                    {datum.renderCell(rowData, columnIndex, rowIndex, datum.hideBreakpoint)}
                  </Fragment>
                );
              }
              return (
                <NumberCell key={columnKey} hideBreakpoint={datum.hideBreakpoint}>
                  {rowData}
                </NumberCell>
              );
            })}
        </tr>
      );
    });
    return rows;
  }

  private getSortedTableData() {
    const { data, disableSort, disableDefaultSort, defaultSortKey } = this.props;
    if (disableSort || (disableDefaultSort && !this.explicitlySorted)) {
      return data;
    }
    const sortKey = this.getSortKey();
    if (!sortKey) {
      return data;
    }
    const sortOrder = this.getSortOrder();
    return sortTableData(data, sortKey, sortOrder, defaultSortKey);
  }

  private renderDownloadLink() {
    const { disableDownloadLink, DownloadLink } = this.props;
    if (disableDownloadLink) {
      return;
    }
    const sortedData = this.getSortedTableData();
    const sheetData = prepareDataForDownload(sortedData);
    return <LinkWrap>{DownloadLink || <DownloadExcelLink dataBySheet={{ data: sheetData }} />}</LinkWrap>;
  }

  public render() {
    const { data, onSortKeyChange, onSortOrderChange, disableSort, title, buttons } = this.props;
    const sortKey = this.getSortKey();

    return (
      <div style={{ overflowX: 'auto' }}>
        {title && (
          <TitleRow>
            {typeof title === 'string' ? <NiceTitle>{title}</NiceTitle> : <>{title}</>}
            {buttons}
          </TitleRow>
        )}
        <Table enableRowSelection={!!this.props.enableRowSelection} styleOverride={this.props.styleOverride}>
          <Thead>
            {this.props.extraHeaderRow}
            <tr>
              {data
                .filter(datum => !datum.exportOnly)
                .map((datum, index) => {
                  if (this.props.hideHeaderRow) {
                    return <></>;
                  }
                  return (
                    <HeaderCell
                      selected={sortKey === datum.key}
                      hideBreakpoint={datum.hideBreakpoint}
                      key={`header-${index}-${datum.key}`}
                      onClick={() => {
                        this.explicitlySorted = true;
                        if (sortKey === datum.key) {
                          const newSortOrder =
                            this.state.sortOrder === SortOrder.ASCENDING ? SortOrder.DESCENDING : SortOrder.ASCENDING;
                          this.setState({ sortOrder: newSortOrder });
                          if (onSortOrderChange) {
                            onSortOrderChange(newSortOrder);
                          }
                        } else {
                          this.setState({ sortKey: datum.key });
                          if (onSortKeyChange) {
                            onSortKeyChange(datum.key);
                          }
                        }
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: this.convertTitleAlignToFlexJustify(datum.titleAlign),
                        }}
                      >
                        <HoverableTitle sortable={!disableSort} style={{ textAlign: datum.titleAlign }}>
                          {datum.title}
                        </HoverableTitle>
                        {!isScreenshotMode() && datum.titleTooltip && datum.titleTooltip}
                      </div>
                    </HeaderCell>
                  );
                })}
            </tr>
          </Thead>
          <tbody>
            {this.renderRows()}
            {this.renderFooter()}
          </tbody>
        </Table>
        {this.renderEmptyState()}
        {!isScreenshotMode() && this.renderDownloadLink()}
      </div>
    );
  }

  private convertTitleAlignToFlexJustify(titleAlign?: 'left' | 'right' | 'center') {
    switch (titleAlign) {
      case 'left':
        return 'flex-start';
      case 'right':
        return 'flex-end';
      case 'center':
        return 'center';
      case undefined:
      default:
        return 'center';
    }
  }

  private renderEmptyState() {
    const { data, emptyState } = this.props;
    if (!data || data[0].values.length > 0) {
      return;
    }
    const content = emptyState || 'Table is empty';
    return <EmptyState>{content}</EmptyState>;
  }

  private renderFooter() {
    const { data, showFooter } = this.props;
    if (!showFooter) {
      return null;
    }

    const dataToShow = data.filter(datum => !datum.exportOnly);

    const totalRowCount = dataToShow[0].values.length;

    const showShowMoreRow = shouldShowShowMore(this.state.shown, totalRowCount, this.props.limit);

    return (
      <>
        {showShowMoreRow && (
          <ShowMoreRow>
            <td>
              <ShowMoreCell
                totalAmount={totalRowCount}
                currentlyShown={this.state.shown}
                onClick={() => {
                  this.setState({ shown: totalRowCount });
                }}
              />
            </td>
            {dataToShow.slice(1).map((_datum, index) => {
              return <td key={index} />;
            })}
          </ShowMoreRow>
        )}
        <tr key="footer">
          {dataToShow.map((datum, index) => {
            return (
              <FooterCell
                key={index}
                style={{ textAlign: datum.titleAlign, border: 'none' }}
                styleOverride={this.props.footerStyleOverride}
              >
                {!!datum.footerFunction && datum.footerFunction(datum.values)}
              </FooterCell>
            );
          })}
        </tr>
      </>
    );
  }
}

const ShowMoreCell = (props: { totalAmount: number; currentlyShown: number; onClick: () => void }) => {
  return (
    <Root onClick={props.onClick}>
      <AwesomeActionIcon icon={<InterfaceArrowsVerticalExpand1 />} />
      <ShowMoreText>Show {props.totalAmount - props.currentlyShown} more</ShowMoreText>
    </Root>
  );
};

const Root = styled.div`
  ${getBouLinkStyle()}
  ${fluidSpacing('margin-top', BouSpacingLevel.TINY)}
  ${fluidSpacing('margin-bottom', BouSpacingLevel.TINY)}
  display: flex;
  align-items: center;
  &:hover {
    color: ${BouColor.TOOL_HOVER_DARK_GRAY};
  }
`;

const ShowMoreText = styled.div`
  margin-left: 0.3em;
  margin-top: 0.15em;
`;

function shouldShowShowMore(shown: number, totalRowCount: number, limitOptions: TableLimitOptions | undefined) {
  if (!limitOptions || !limitOptions.showMore) {
    return false;
  }
  if (shown >= totalRowCount) {
    return false;
  }
  return true;
}

function getInitialCountToShow(totalRowCount: number, limitOptions: TableLimitOptions | undefined) {
  if (!limitOptions) {
    return totalRowCount;
  }
  const overflow = limitOptions.overflow || 0;
  if (totalRowCount > limitOptions.amount + overflow) {
    return limitOptions.amount;
  }
  return totalRowCount;
}

export const TitleRow = styled.div`
  ${fluidSpacing('min-height', BouSpacingLevel.MEDIUM)}
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

export const EmptyState = styled.div`
  border-top: 2px solid #eeeeee;
  padding-left: 24px;
  padding-right: 24px;
  padding-bottom: 24px;
  padding-top: 24px;
  font-size: 16px;
  color: ${BouColor.TOOL_TEXT_DARK_GRAY};
  min-height: 140px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f9f9f9;
`;

const ShowMoreRow = styled.tr``;
